/**
 * Превью приложенной картинки и просмотр в полный размер.
 *
 * Байты приходят по подписанной ссылке «на просмотр»: сервер отдаёт
 * картинку inline. Ссылка живёт минуты — если к открытию модалки она
 * истекла и браузер не взял картинку из памяти, запрашивается новая.
 */

import { ImageOff, Paperclip } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { resolveApiUrl } from "@/shared/api/http";
import { Button, Modal } from "@/shared/ui";
import { Download } from "@/shared/ui/motionIcons";

import { type Attachment, useAttachmentOpenUrlMutation } from "@taskflow/client-core/tasks";

type State = { kind: "loading" } | { kind: "ready"; url: string } | { kind: "failed" };

export function AttachmentImage({
  file,
  onDownload,
}: {
  file: Attachment;
  onDownload: () => void;
}) {
  const [openUrl, link] = useAttachmentOpenUrlMutation();
  const [broken, setBroken] = useState(false);
  const [viewing, setViewing] = useState(false);
  // Одна повторная попытка на истёкшую ссылку, а не бесконечный цикл ошибок.
  const retried = useRef(false);

  useEffect(() => {
    const request = openUrl({ id: file.id });
    return () => request.abort();
  }, [openUrl, file.id]);

  const url = link.data === undefined ? null : resolveApiUrl(link.data.url);
  const state: State =
    broken || link.isError || (link.isSuccess && url === null)
      ? { kind: "failed" }
      : url === null
        ? { kind: "loading" }
        : { kind: "ready", url };

  const handleError = () => {
    if (retried.current) {
      setBroken(true);
      return;
    }
    retried.current = true;
    void openUrl({ id: file.id });
  };

  if (state.kind === "failed") {
    return (
      <span className="flex size-10 shrink-0 items-center justify-center rounded border border-line text-faint">
        <Paperclip className="size-4" aria-hidden />
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        className="size-10 shrink-0 overflow-hidden rounded border border-line bg-sunken focus-visible:outline-2 focus-visible:outline-accent"
        aria-label={`Открыть ${file.filename}`}
        title="Открыть в полном размере"
        disabled={state.kind !== "ready"}
        onClick={() => setViewing(true)}
      >
        {state.kind === "ready" && (
          <img
            src={state.url}
            alt=""
            className="size-full object-cover"
            loading="lazy"
            onError={handleError}
          />
        )}
      </button>

      <Modal
        open={viewing}
        onClose={() => setViewing(false)}
        title={file.filename}
        width="xl"
        footer={
          <Button size="sm" variant="secondary" icon={Download} onClick={onDownload}>
            Скачать
          </Button>
        }
      >
        {state.kind === "ready" ? (
          <img
            src={state.url}
            alt={file.filename}
            className="mx-auto block max-h-[calc(100dvh-12rem)] max-w-full object-contain"
            onError={handleError}
          />
        ) : (
          <p className="flex items-center justify-center gap-2 py-10 text-[13px] text-faint">
            <ImageOff className="size-4" aria-hidden />
            Не удалось загрузить изображение
          </p>
        )}
      </Modal>
    </>
  );
}
